import {
  CONTRACT_STATUSES,
  INVOICE_BILLING_STATUSES,
  INVOICE_FILTERABLE_FIELDS,
  INVOICE_REVIEW_STATUSES,
  InvoiceFilter,
  SYNC_STATUSES,
  MONTHS,
} from '@vy/types';
import { isArray, isString, vyDate } from '@vy/utils';
import { getBillingStatusLabel } from '../invoices-page-components/InvoiceBillingStatusLabel';

const CONTRACT_STATUS_LABELS: Record<string, string> = {
  [CONTRACT_STATUSES.InReview]: 'In Review',
  [CONTRACT_STATUSES.Active]: 'Active',
  [CONTRACT_STATUSES.Inactive]: 'Inactive',
  [CONTRACT_STATUSES.Expired]: 'Expired',
  [CONTRACT_STATUSES.Terminated]: 'Terminated',
  [CONTRACT_STATUSES.PendingTermination]: 'Pending Termination',
  [CONTRACT_STATUSES.Error]: 'Error',
};

const REVIEW_STATUS_LABELS: Record<string, string> = {
  [INVOICE_REVIEW_STATUSES.Draft]: 'Draft',
  [INVOICE_REVIEW_STATUSES.InReview]: 'In Review',
  [INVOICE_REVIEW_STATUSES.Approved]: 'Approved',
  [INVOICE_REVIEW_STATUSES.Future]: 'Future',
};

const BILLING_STATUS_LABELS: Record<string, string> = {
  [INVOICE_BILLING_STATUSES.None]: getBillingStatusLabel(INVOICE_BILLING_STATUSES.None),
  [INVOICE_BILLING_STATUSES.Paid]: getBillingStatusLabel(INVOICE_BILLING_STATUSES.Paid),
  [INVOICE_BILLING_STATUSES.Rejected]: getBillingStatusLabel(INVOICE_BILLING_STATUSES.Rejected),
  [INVOICE_BILLING_STATUSES.PendingPayment]: getBillingStatusLabel(INVOICE_BILLING_STATUSES.PendingPayment),
  [INVOICE_BILLING_STATUSES.Overdue]: getBillingStatusLabel(INVOICE_BILLING_STATUSES.Overdue),
};

const VALUE_LABELS_BY_FIELD: Record<string, Record<string, string>> = {
  [INVOICE_FILTERABLE_FIELDS.INVOICE_CONTRACT_STATUS_FIELD]: CONTRACT_STATUS_LABELS,
  [INVOICE_FILTERABLE_FIELDS.INVOICE_REVIEW_STATUS_FIELD]: REVIEW_STATUS_LABELS,
  [INVOICE_FILTERABLE_FIELDS.INVOICE_BILLING_STATUS_FIELD]: BILLING_STATUS_LABELS,
};

export const getMonthShortName = (month: string): string => {
  const monthIndex = parseInt(month, 10);
  return MONTHS[monthIndex].slice(0, 3) || '';
};

export const formatDateDisplay = (date: { month: string; year: number }): string => {
  return `${getMonthShortName(date.month)} ${date.year}`;
};

export const DATE_FIELDS: string[] = [
  INVOICE_FILTERABLE_FIELDS.INVOICE_ISSUED_AT_FIELD,
  INVOICE_FILTERABLE_FIELDS.INVOICE_DUE_DATE_FIELD,
  INVOICE_FILTERABLE_FIELDS.INVOICE_LAST_SENT_FIELD,
];

function getValueLabel(field: string, value: string, subsidiaryNameById: Record<string, string> = {}): string {
  if (field === INVOICE_FILTERABLE_FIELDS.INVOICE_SUBSIDIARY_FIELD) {
    return subsidiaryNameById[value] ?? value;
  }

  const labelsForField = VALUE_LABELS_BY_FIELD[field];

  if (labelsForField) {
    return labelsForField[value] ?? value;
  }

  return value;
}

export function formatFilterValue(filter: InvoiceFilter, subsidiaryNameById: Record<string, string> = {}): string {
  if (isArray(filter.value)) {
    return filter.value.map((value) => getValueLabel(filter.field, String(value), subsidiaryNameById)).join(', ');
  }

  if (isString(filter.value)) {
    const isDateField = DATE_FIELDS.includes(filter.field);

    if (isDateField) {
      try {
        const date = new Date(filter.value);
        return vyDate(date).format('MMM D');
      } catch {
        return filter.value;
      }
    }

    if (filter.field === INVOICE_FILTERABLE_FIELDS.INVOICE_SYNC_INTEGRATION_FIELD) {
      const syncStatus = Object.values(SYNC_STATUSES).find((status) => status === filter.value);
      return syncStatus === SYNC_STATUSES.Synced ? 'Synced' : filter.value;
    }

    return getValueLabel(filter.field, filter.value, subsidiaryNameById);
  }

  if (typeof filter.value === 'boolean') {
    return filter.value ? 'Yes' : 'No';
  }

  return String(filter.value);
}

export function getFilterLabel(filter: InvoiceFilter): string {
  const fieldLabels: Record<string, string> = {
    [INVOICE_FILTERABLE_FIELDS.INVOICE_ISSUED_AT_FIELD]: 'Issue date',
    [INVOICE_FILTERABLE_FIELDS.INVOICE_SYNC_INTEGRATION_FIELD]: 'Sync integration',
    [INVOICE_FILTERABLE_FIELDS.INVOICE_LAST_SENT_FIELD]: 'Sent reminder',
    [INVOICE_FILTERABLE_FIELDS.INVOICE_BILLING_STATUS_FIELD]: 'Billing Status',
    [INVOICE_FILTERABLE_FIELDS.INVOICE_REVIEW_STATUS_FIELD]: 'Workflow',
    [INVOICE_FILTERABLE_FIELDS.INVOICE_DUE_DATE_FIELD]: 'Due Date',
    [INVOICE_FILTERABLE_FIELDS.INVOICE_CONTRACT_STATUS_FIELD]: 'Contract Status',
    [INVOICE_FILTERABLE_FIELDS.INVOICE_CURRENCY_FIELD]: 'Currency',
    [INVOICE_FILTERABLE_FIELDS.INVOICE_AMOUNT_FIELD]: 'Amount',
    [INVOICE_FILTERABLE_FIELDS.INVOICE_NAME_FIELD]: 'Period',
    [INVOICE_FILTERABLE_FIELDS.INVOICE_CUSTOMER_ID_FIELD]: 'Customer ID',
    [INVOICE_FILTERABLE_FIELDS.INVOICE_SUBSIDIARY_FIELD]: 'Subsidiary',
    [INVOICE_FILTERABLE_FIELDS.INVOICE_OVERAGE_FIELD]: 'Overage',
    [INVOICE_FILTERABLE_FIELDS.INVOICE_MANUAL_REVIEW_FIELD]: 'Manual Review',
    [INVOICE_FILTERABLE_FIELDS.INVOICE_YEARLY_COMMITMENT_FIELD]: 'Yearly Commitment',
    [INVOICE_FILTERABLE_FIELDS.INVOICE_METERED_FIELD]: 'Usage',
  };

  return fieldLabels[filter.field] || filter.field;
}
